import React, { useMemo, useContext } from 'react';
import { Eventbus } from 'app/src/data/Eventbus';
import GlobalEvents from 'app/GlobalEvents';
import Decimal from 'app/src/data/base/Decimal';
import SaleManager from 'app/src/payments/SaleManager';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import Localize from 'app/src/localize/Localize';

import PaymentTotalViewController from 'app/src/controllers/payments/PaymentTotalViewController';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import { isEmpty } from 'lodash';

export interface IPaymentStateProps {
  saleManager: TSFixMe;
  denominations: TSFixMe;
  userDefinedPaymentTypes: TSFixMe;
  paymentMethod: TSFixMe;
  onPaymentChanged: TSFixMe;
  canRetrieveGateway: TSFixMe;
  paymentProvider: TSFixMe;
  selectedTerminalId: TSFixMe;
  setSelectedTerminalId: TSFixMe;
}
export enum PaymentViewEnum {
  PaymentDefaultView = '/',
  CreditCardView = '/CreditCardView',
}
const PaymentContext = React.createContext<IPaymentStateProps>({} as any);

export const PaymentStateProvider = ({ children }: TSFixMe) => {
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
  const [saleManager, setSaleManager] = React.useState<SaleManager>(null);

  const [paymentProvider, setPaymentProvider] = React.useState(null);
  const [paymentMethod, setPaymentMethod] = React.useState({});
  const [denominations, setDenominations] = React.useState([]);
  const [userDefinedPaymentTypes, setUserDefinedPaymentTypes] = React.useState([]);
  const [canRetrieveGateway, setCanRetrieveGateway] = React.useState(true);

  const [selectedTerminalId, setSelectedTerminalId] = React.useState('select-terminal');

  /**
   * Adapted from PaymentDefault View ->_setViewPayment
   * except we handle hidden, disable and name as well
   * */
  const updatePaymentValue = React.useCallback(
    ({
      paymentTypeID,
      amount: internalAmount,
      isDisabled = false,
      hidden = false,
      name: internalName,
    }) => {
      let amount: Decimal = internalAmount;
      if (amount && !amount.isNaN()) {
        if (saleManager.isRefundSale()) {
          // for refunds we want the value reflected in the UI to be
          // positive
          amount = amount.abs();
        }
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        let currPaymentMethod = paymentMethod[paymentTypeID] || {};
        const name = internalName || currPaymentMethod.name;

        currPaymentMethod = {
          ...currPaymentMethod,
          disable: isDisabled,
          hidden,
          name,
          amount,
        };
        setPaymentMethod({
          ...paymentMethod,
          [paymentTypeID]: currPaymentMethod,
        });
      } else {
        throw new Error('Amount is not a Decimal');
      }
    },
    [paymentMethod, saleManager]
  );

  React.useEffect(() => {
    /**
     * Initializes the payment Type list
     */
    const initPaymentMethodList = (saleManager: TSFixMe) => {
      const cashPaymentTypeID = PaymentTypeManager.getPaymentIdForType(
        PaymentTypeManager.Types.CASH
      );
      const cashModel = {
        paymentTypeID: cashPaymentTypeID,
        name: 'Cash',
      };
      const creditAccountPaymentTypeID = PaymentTypeManager.getPaymentIdForType(
        PaymentTypeManager.Types.CREDIT_ACCOUNT
      );
      const creditAccountModel = {
        paymentTypeID: creditAccountPaymentTypeID,
        name: 'Credit Account',
      };
      const paymentMethodList = PaymentTypeManager.getUserDefinedPaymentTypes();
      let paymentList: TSFixMe;

      paymentMethodList.add(cashModel);
      paymentMethodList.add(creditAccountModel);
      paymentMethodList.forEach(paymentType => {
        const name = paymentType.get('name');
        const paymentTypeID = paymentType.get('paymentTypeID');
        const paymentAmount = saleManager.getTotalPaymentsOfType(paymentTypeID);
        const amount = paymentAmount.isZero() ? new Decimal(0) : new Decimal(paymentAmount);
        const requireCustomer = paymentType.get('requireCustomer');
        paymentList = {
          ...paymentList,
          [paymentTypeID]: { paymentTypeID, amount, name, requireCustomer },
        };
      });
      return paymentList;
    };

    /**
     * Adapted version of _setUserDefinedPayments from PaymentDefaultViewController
     */
    const addUserDefinedPaymentMethods = () => {
      const userDefinedPaymentTypes = PaymentTypeManager.getUserDefinedPaymentTypes();
      setUserDefinedPaymentTypes(userDefinedPaymentTypes.toJSON());
    };
    /**
     *  Adapted from showPayments in PaymentController.
     * Current it only supports binding the controller
     */
    const initPaymentControllers = (saleManager: TSFixMe) => {
      const paymentTotalViewController = new PaymentTotalViewController(saleManager);
      paymentTotalViewController.show();
    };

    const initPanel = (data: TSFixMe) => {
      const { saleManager: internalSaleManager, paymentProvider: internalPaymentProvider } = data;
      setCanRetrieveGateway(internalPaymentProvider.canRetrieveGateway);
      setPaymentProvider(internalPaymentProvider);
      setSaleManager(internalSaleManager);
      setDenominations(Localize.getLocalizedDenominations().toJSON());
      setPaymentMethod(initPaymentMethodList(internalSaleManager));
      initPaymentControllers(internalSaleManager);
      addUserDefinedPaymentMethods();
    };

    Eventbus.listen({
      eventName: GlobalEvents.Events.register.SHOW_PAYMENTS,
      callbackFn: initPanel,
    });
    return () => {
      Eventbus.unSubscribe(GlobalEvents.Events.register.SHOW_PAYMENTS, initPanel);
    };
  }, []);

  /**
   * From _resetViewToPreviousAmount from Payment Default View Controller
   * Reset the UI amount displayed to the last accepted amount in the saleManager
   *
   * @param paymentTypeID {string|number} The ID of the Payment Type
   * @private
   */
  const resetViewToPreviousAmount = React.useCallback(
    paymentTypeID => {
      const previousAmount = saleManager.getTotalPaymentsOfType(paymentTypeID);
      updatePaymentValue({ paymentTypeID, amount: previousAmount });
    },
    [saleManager, updatePaymentValue]
  );

  const calculateAmountByPaymentTypeName = React.useCallback(
    ({ paymentAmount, paymentTypeName }) => {
      try {
        let amount = isEmpty(paymentAmount) ? new Decimal(0) : new Decimal(paymentAmount);
        // Determine how much balance would be left to pay if you exclude any
        // prior value of the payment that has changed.
        amount = amount.abs();

        if (paymentTypeName === PaymentTypeManager.Types.CREDIT_ACCOUNT) {
          let balance;
          const availableCredit = saleManager.availableCreditBalance;

          // amount should not exceed the available credit.
          // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
          if (availableCredit.isLessThan(amount)) {
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'Decimal | null' is not assignable to type 'D... Remove this comment to see the full error message
            amount = availableCredit;
          }

          // there's no change,  amount should not exceed
          // a positive balance.
          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
          if (amount.isGreaterThan(balance) && balance.isGreaterThan(new Decimal(0))) {
            // update the input to the correct balance.
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'undefined' is not assignable to type 'Decima... Remove this comment to see the full error message
            amount = balance;
          }
        }
        return amount;
      } catch (err) {
        return new Decimal(NaN);
      }
    },
    [saleManager]
  );

  /**
   * From onPaymentChanged from Payment Default View Controller
   * Event handler for when the value of a payment field is modified.
   *
   * @param paymentTypeID {number}    The payment type ID of the payment type field that has changed
   * @param paymentAmount {Decimal}   The new amount
   */
  const onPaymentChanged = React.useCallback(
    (paymentTypeID: number, paymentAmount) => {
      let amount: Decimal = new Decimal(NaN);
      const paymentTypeName = PaymentTypeManager.getPaymentTypeByAttributes({
        paymentTypeID,
      });
      amount = calculateAmountByPaymentTypeName({ paymentAmount, paymentTypeName });

      if (!amount.isNaN()) {
        let payment;
        switch (paymentTypeName) {
          case PaymentTypeManager.Types.USER_DEFINED:
          case PaymentTypeManager.Types.ECOM:
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Object' is not assignable to par... Remove this comment to see the full error message
            payment = PaymentFactory.createPayment(paymentTypeName, amount, paymentTypeID);
            break;

          case PaymentTypeManager.Types.CREDIT_ACCOUNT:
            payment = PaymentFactory.createPayment(
              // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Object' is not assignable to par... Remove this comment to see the full error message
              paymentTypeName,
              amount,
              saleManager.creditAccountID
            );
            break;

          default:
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Object' is not assignable to par... Remove this comment to see the full error message
            payment = PaymentFactory.createPayment(paymentTypeName, amount);
        }

        const result = saleManager.validatePayment(payment);
        if (result.error === SaleManager.Errors.USER_FACING && !saleManager.isRefundSale()) {
          // TODO: replace this mechanism to remove the dependency on the legacy implementation
          window.alertUser(result.msg);

          // revert the payment field to the current value
          resetViewToPreviousAmount(paymentTypeID);
        } else {
          if (saleManager.hasExistingPaymentOfType(payment)) {
            saleManager.updatePaymentAmount(payment);
          } else {
            saleManager.addPayment(payment);
          }
          updatePaymentValue({ paymentTypeID, amount });
        }
      } else {
        // revert the payment field to the current value
        resetViewToPreviousAmount(paymentTypeID);
      }
    },
    [calculateAmountByPaymentTypeName, resetViewToPreviousAmount, saleManager, updatePaymentValue]
  );

  const props = useMemo(() => {
    return {
      saleManager,
      paymentMethod,
      userDefinedPaymentTypes,
      onPaymentChanged,
      denominations,
      canRetrieveGateway,
      paymentProvider,
      selectedTerminalId,
      setSelectedTerminalId,
    };
  }, [
    saleManager,
    paymentMethod,
    userDefinedPaymentTypes,
    onPaymentChanged,
    denominations,
    canRetrieveGateway,
    paymentProvider,
    selectedTerminalId,
  ]);

  return <PaymentContext.Provider value={props}>{children}</PaymentContext.Provider>;
};

export const usePaymentContext = () => {
  return useContext(PaymentContext);
};
